import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";

import { SignalEventsOccurrence } from "shared/api/signalEvents/api";
import useSignalEventOccurrencesSchema from "shared/schemas/signalEventOccurrencesSchema";
import { EventTypeEnum } from "shared/types";

import { DETAILS_RELATED_EVENTS_ID } from "pages/Dashboard/components/constants";
import AiSummaryAndFields from "pages/Dashboard/components/EventDetails/AiSummaryAndFields";
import RelatedEvents from "pages/Dashboard/components/EventDetails/ClaimDetails/RelatedEvents";
import EventDetailsHeader from "pages/Dashboard/components/EventDetails/EventDetailsHeader";
import RelatedEventsAndSmartGroups from "pages/Dashboard/components/EventDetails/RelatedEventsAndSmartGroups";
import TableDrawer from "pages/Dashboard/components/EventDetails/TableDrawer";
import AttributesCard from "pages/shared/Dashboard/AttributesCard";
import HeaderFields from "pages/shared/Dashboard/HeaderFields";
import { LandingPageViewConfig } from "pages/shared/Dashboard/types";
import { getDefaultDetailsConfig } from "pages/shared/Dashboard/utils";
import { getAttributeValue } from "pages/shared/schemaDefinitions/utils";

import Typography from "features/components/Typography";

import SkeletonLoader from "./SkeletonLoader";

interface Props {
  signalEvent: SignalEventsOccurrence;
  onNext: () => void;
  onPrevious: () => void;
  config: LandingPageViewConfig;
  count: number;
  signalEventIndex: number;
}

interface PropsDrawer {
  signalEvent?: SignalEventsOccurrence;
  onNext: () => void;
  onPrevious: () => void;
  openedIndex: number | null;
  onClose: () => void;
  config: LandingPageViewConfig;
  count: number;
  signalEventIndex: number;
  isLoading?: boolean;
}

const SignalEventDetailsDrawer = ({
  signalEvent,
  config,
  openedIndex,
  onNext,
  onPrevious,
  onClose,
  count,
  signalEventIndex,
  isLoading,
}: PropsDrawer) => {
  const [, setSearchParams] = useSearchParams();

  useEffect(() => {
    setSearchParams(
      (prev) => {
        if (openedIndex === null) {
          prev.delete("quickViewIndex");
        } else if (signalEvent) {
          prev.set("quickViewIndex", String(signalEventIndex));
        }

        return prev;
      },
      { replace: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    openedIndex,
    signalEvent?.signalEventID,
    signalEvent?.recordedAt,
    signalEventIndex,
    setSearchParams,
  ]);

  if (!signalEvent && isLoading) {
    return (
      <TableDrawer open={openedIndex != null} onClose={onClose}>
        <SkeletonLoader></SkeletonLoader>
      </TableDrawer>
    );
  }

  if (!signalEvent) {
    return null;
  }

  return (
    <TableDrawer open={openedIndex != null} onClose={onClose}>
      <SignalEventDetails
        signalEvent={signalEvent}
        onNext={onNext}
        onPrevious={onPrevious}
        config={config}
        count={count}
        signalEventIndex={signalEventIndex}
      />
    </TableDrawer>
  );
};

const SignalEventDetails = ({
  config,
  signalEvent,
  onNext,
  onPrevious,
  count,
  signalEventIndex,
}: Props) => {
  const { t } = useTranslation();
  const { getDisplayLabel, getSchemaForAccessor } =
    useSignalEventOccurrencesSchema();

  const detailsConfig =
    config.eventDetails ?? getDefaultDetailsConfig(config.eventType);
  const dateField = config.dateField || "recordedAt";

  const notAvailable = t("common:not_available");
  const signalEventIdLabel = t("schema:dashboard.signal_event_id");

  let shownTitle = getDisplayLabel(
    detailsConfig.titleField,
    signalEventIdLabel
  );
  let shownID: any = getAttributeValue(
    detailsConfig.titleField,
    signalEvent,
    getSchemaForAccessor,
    t
  );

  if (!shownID || shownID === "" || shownID === notAvailable) {
    shownTitle = getDisplayLabel("concern", t("schema:dashboard.concern"));
    shownID = signalEvent.concern;
  }

  if (!shownID || shownID === "" || shownID === notAvailable) {
    shownTitle = getDisplayLabel("signalEventID", signalEventIdLabel);
    shownID = signalEvent.signalEventID;
  }

  if (!shownID || shownID === "" || shownID === notAvailable) {
    shownTitle = getDisplayLabel(detailsConfig.titleField, signalEventIdLabel);
    shownID = notAvailable;
  }

  return (
    <div className="flex flex-col h-full text-xs">
      <EventDetailsHeader
        config={config}
        event={signalEvent}
        dateField={dateField}
        onNext={onNext}
        onPrevious={onPrevious}
        currentIndex={signalEventIndex}
        count={count}
        getDisplayLabel={getDisplayLabel}
        getSchemaForAccessor={getSchemaForAccessor}
      />

      <div className="overflow-auto flex-1 bg-white">
        {/*main*/}
        <div className="bg-white">
          <div className="p-4 px-8 flex flex-col gap-4">
            <div className="border-b-2 border-gray-700 pb-2">
              <div className="font-bold pb-2">{shownTitle}</div>
              <div className="flex justify-between items-start -mt-2">
                <Typography variant="h2">{shownID}</Typography>
                <HeaderFields
                  fields={detailsConfig.headerFields ?? []}
                  item={signalEvent}
                  getDisplayLabel={getDisplayLabel}
                  getSchemaForAccessor={getSchemaForAccessor}
                />
              </div>
            </div>
            <div className="flex flex-row gap-12 h-fit">
              <div className="flex-1">
                <div className="flex-3 h-full flex flex-col text-xs">
                  <div className="flex flex-col">
                    <div className="bg-purple-50 rounded-lg flex-1 font whitespace-pre-wrap">
                      <RelatedEventsAndSmartGroups
                        event={signalEvent}
                        config={config}
                      />
                    </div>
                  </div>
                  <AiSummaryAndFields
                    event={signalEvent}
                    config={config}
                    getDisplayLabel={getDisplayLabel}
                    getSchemaForAccessor={getSchemaForAccessor}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {detailsConfig.cardRows.map((cardRow, index) => (
          <div className="flex flex-row gap-4 p-4 px-8 pb-0" key={index}>
            {cardRow.map((card, index) => (
              <div className={card.containerClass} key={index}>
                <AttributesCard
                  item={signalEvent}
                  attributes={card.fields}
                  title={card.title}
                  direction={card.type}
                  getSchemaForAccessor={getSchemaForAccessor}
                />
              </div>
            ))}
          </div>
        ))}

        <div className="mt-6" id={DETAILS_RELATED_EVENTS_ID}>
          {detailsConfig.relatedEvents.map((group, index) => (
            <div
              id={`${DETAILS_RELATED_EVENTS_ID}-${index}`}
              key={`${group.title}_${index}`}
            >
              <RelatedEvents
                config={group}
                event={signalEvent}
                vin={signalEvent.VIN}
                eventType={EventTypeEnum.SIGNAL_EVENT}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SignalEventDetailsDrawer;
